import {ProcessedNode} from "./../types/node";
import tailwindConfig from "../../tailwind.config.js";
import resolveConfig from "tailwindcss/resolveConfig";
import {
  SettingParam,
  SettingParamTypes,
  FlowNodeTypes,
  FlowNodeData,
  PromptAction,
  ActionTypes,
} from "@/types/node";
import {FlowNodeSetting} from "@/types/node";
import {Node, Edge, Position} from "@xyflow/react";
import {FlowNodesData} from "@/pages/flow/NodeTypes/NodesData";
import {CustomAction} from "@/api/services/customAction.js";
import {ApiError} from "./apiClient";
import {ActionTypeModel, CustomActionRecordModel} from "@/api/models";
import {BaseAction} from "@/types/api";
import {getAction} from "@/api/services/queries";
import {actionCustomTransform} from "@/api/transform/actions/ActionCustomTransform";

export type Direction = "TB" | "LR" | "RL" | "BT";

export function capitalize(str: string): string {
  if (!str) return "";
  return str
    .toLowerCase()
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export const capitalizeFirst = (str: string) =>
  str?.charAt(0).toUpperCase() + str?.slice(1);

export function generateId() {
  return (
    new Date().getTime().toString(36) + Math.random().toString(36).slice(2)
  );
}

export const getTailwindColor = (colorClass: string) => {
  if (!colorClass) return "";
  const fullConfig = resolveConfig(tailwindConfig);
  const tailwindColors = fullConfig.theme.colors;
  // Convert Tailwind class name to actual color value
  // Make this more function more dynamic to support more prefixes
  const [colorName, colorValue] = colorClass.replace("bg-", "").split("-");
  return (
    tailwindColors[colorName][colorValue] ||
    tailwindColors[colorName] ||
    colorClass
  );
};

export const getQueryParams = () => {
  return new URLSearchParams(window.location.search);
};

export const findPromptValues = nodes => {
  const result = [];

  const extractValues = obj => {
    if (typeof obj !== "object" || obj === null) return;

    Object.values(obj).forEach(value => {
      if (Array.isArray(value)) {
        value.forEach(item => extractValues(item));
      } else if (typeof value === "object") {
        extractValues(value);
      }
    });

    if (
      obj.type === SettingParamTypes.PROMPT &&
      obj.value &&
      obj.value.length > 0
    ) {
      result.push(obj.value);
    }
  };

  nodes.forEach(node => {
    if (node.data?.settings) {
      extractValues(node.data.settings);
    }
  });

  return result;
};

export const getSettingValueByKey = (
  settings: FlowNodeSetting[],
  keyName: string,
): SettingParam["value"] => {
  const deepSearch = (obj: any): any => {
    if (Array.isArray(obj)) {
      for (const item of obj) {
        const result = deepSearch(item);
        if (result !== undefined) return result;
      }
    } else if (obj && typeof obj === "object") {
      if (obj.key === keyName)
        return typeof obj.value === "string" ? obj.value.trim() : obj.value;

      for (const key in obj) {
        const result = deepSearch(obj[key]);
        if (result !== undefined) return result;
      }
    }
    return undefined;
  };

  return deepSearch(settings) ?? null;
};

export const getSettingValueByKeys = (settings: any[], keys: string[]) => {
  return settings?.filter(setting => keys.includes(setting.params?.[0]?.key));
};

export const getNodesByType = (nodes: Node[], type: FlowNodeTypes) => {
  return nodes.filter(node => node.type === type) || [];
};

export const getFlowNodeSetting = (node: Node, key: string) => {
  return node.data.settings[key] || null;
};

export const getNodeElement = (nodeId: string) => {
  return window.__flowBuilderIframeDoc.querySelector(
    `[data-id="${nodeId}"]`,
  ) as HTMLElement;
};

export const getAllActionIds = nodes => {
  const actionIds = new Set(
    nodes.flatMap(
      node =>
        node.data?.settings?.flatMap(setting =>
          setting.params
            ?.filter(
              param =>
                param.type === SettingParamTypes.ACTIONS &&
                Array.isArray(param.value),
            )
            .flatMap(param => param.value.map(action => action.action_id)),
        ) || [],
    ),
  );
  return Array.from(actionIds);
};

export const getActionsFromNodes = (
  nodes: Node<FlowNodeData>[],
  actionType?: ActionTypes,
): PromptAction[] => {
  const actionNodeTypeMapping = {
    [FlowNodeTypes.ACTION]: ActionTypeModel.CUSTOM,
    [FlowNodeTypes.BOOKING]: ActionTypeModel.BOOKING,
    [FlowNodeTypes.TRANSFER]: ActionTypeModel.TRANSFER,
  };

  let actionNodes;
  if (!actionType) {
    actionNodes = nodes.filter(
      node =>
        node.data.type === FlowNodeTypes.ACTION ||
        node.data.type === FlowNodeTypes.BOOKING ||
        node.data.type === FlowNodeTypes.TRANSFER,
    );
  } else {
    const nodeType = Object.keys(actionNodeTypeMapping).find(
      key => actionNodeTypeMapping[key] === actionType,
    );
    actionNodes = nodes.filter(node => node.data.type === nodeType);
  }

  const actions = actionNodes
    .map(node => {
      const action = getSettingValueByKey(
        node.data.settings,
        "action",
      ) as CustomActionRecordModel & {actionRecord: CustomActionRecordModel};

      if (action.action_id && action.type === ActionTypeModel.CUSTOM) {
        return {
          name: action.actionRecord.title,
          http_method: action.actionRecord.apiMethod,
          action_results: action.actionRecord.variables,
          action_id: action.actionRecord.action_id,
        };
      }
    })
    .filter(action => action && action.action_id);

  return actions;
};

export function getSourceHandlePosition(direction: Direction) {
  switch (direction) {
    case "TB":
      return Position.Bottom;
    case "BT":
      return Position.Top;
    case "LR":
      return Position.Right;
    case "RL":
      return Position.Left;
  }
}

export function getTargetHandlePosition(direction: Direction) {
  switch (direction) {
    case "TB":
      return Position.Top;
    case "BT":
      return Position.Bottom;
    case "LR":
      return Position.Left;
    case "RL":
      return Position.Right;
  }
}

function getParams(nodeA, nodeB) {
  const centerA = getNodeCenter(nodeA);
  const centerB = getNodeCenter(nodeB);

  const horizontalDiff = Math.abs(centerA.x - centerB.x);
  const verticalDiff = Math.abs(centerA.y - centerB.y);

  // when the horizontal difference between the nodes is bigger, we use Position.Left or Position.Right for the handle
  const position = centerA.x > centerB.x ? Position.Left : Position.Right;
  // if (horizontalDiff > verticalDiff) {
  // } else {
  //   // here the vertical difference between the nodes is bigger, so we use Position.Top or Position.Bottom for the handle
  //   position = centerA.y > centerB.y ? Position.Top : Position.Bottom;
  // }

  const [x, y] = getHandleCoordsByPosition(nodeA, position);
  return [x, y, position];
}

function getHandleCoordsByPosition(node, handlePosition) {
  // all handles are from type source, that's why we use handleBounds.source here
  const handle = node.internals.handleBounds.source.find(
    h => h.position === handlePosition,
  );

  let offsetX = handle.width / 2;
  let offsetY = handle.height / 2;

  // this is a tiny detail to make the markerEnd of an edge visible.
  // The handle position that gets calculated has the origin top-left, so depending which side we are using, we add a little offset
  // when the handlePosition is Position.Right for example, we need to add an offset as big as the handle itself in order to get the correct position
  switch (handlePosition) {
    case Position.Left:
      offsetX = 0;
      break;
    case Position.Right:
      offsetX = handle.width;
      break;
    case Position.Top:
      offsetY = 0;
      break;
    case Position.Bottom:
      offsetY = handle.height;
      break;
  }

  const x = node.internals.positionAbsolute.x + handle.x + offsetX;
  const y = node.internals.positionAbsolute.y + handle.y + offsetY;

  return [x, y];
}

function getNodeCenter(node) {
  return {
    x: node.internals.positionAbsolute.x + node.measured.width / 2,
    y: node.internals.positionAbsolute.y + node.measured.height / 2,
  };
}

// returns the parameters (sx, sy, tx, ty, sourcePos, targetPos) you need to create an edge
export function getEdgeParams(source, target) {
  const [sx, sy, sourcePos] = getParams(source, target);
  const [tx, ty, targetPos] = getParams(target, source);

  return {
    sx,
    sy,
    tx,
    ty,
    sourcePos,
    targetPos,
  };
}

export const extractBracketedSections = (strings, [open, close]) => {
  const escapedOpen = open.replace(/[-\\^$*+?.()|[\]{}]/g, "\\$&");
  const escapedClose = close.replace(/[-\\^$*+?.()|[\]{}]/g, "\\$&");

  const regex = new RegExp(
    `${escapedOpen}([^${escapedClose}]+)${escapedClose}`,
    "g",
  );
  const uniqueSections = new Set();

  strings.forEach(str => {
    let match;
    while ((match = regex.exec(str)) !== null) {
      uniqueSections.add(`${open}${match[1]}${close}`.replace(/\u200B/g, ""));
    }
  });

  return Array.from(uniqueSections);
};

export const extractHashtagSections = strings => {
  const regex = /#(\S+)(?=\s|$)/g;
  const uniqueHashtags = new Set();

  strings.forEach(str => {
    let match;
    while ((match = regex.exec(str)) !== null) {
      uniqueHashtags.add(`#${match[1]}`.replace(/\u200B/g, ""));
    }
  });

  return Array.from(uniqueHashtags);
};

export const truncatePath = (path: string) => {
  const commands = path.split(/[A-Z]/).filter(Boolean);
  const commandLetters = path.match(/[A-Z]/g);

  if (!commands || !commandLetters) return path;

  const midpoint = Math.floor(commands.length / 2);

  const truncatedCommands = commands.slice(0, midpoint);
  const truncatedPath = truncatedCommands
    .map((cmd, index) => commandLetters[index] + cmd.trim())
    .join(" ");

  return truncatedPath;
};

export const isEmptySettingValue = (value: any): boolean => {
  if (value === undefined || value === null) return true; // Check null/undefined
  if (typeof value === "string" && value.trim() === "") return true; // Check empty string
  if (typeof value === "number") return false; // Numbers are always valid
  if (typeof value === "boolean") return false; // Booleans are always valid
  if (Array.isArray(value)) return value.length === 0; // Check empty array
  if (typeof value === "object") return Object.keys(value).length === 0; // Check empty object

  return false;
};

export const restoreNodeSettings = (
  nodes: Node[],
  defaultNodes: Node<FlowNodeData>[] = [],
): Node<FlowNodeData>[] => {
  return (nodes || defaultNodes).map((node: Node<FlowNodeData>) => {
    const blueprint = FlowNodesData[node.type]?.blueprint || {};
    const blueprintSettings = blueprint.data?.settings || [];

    const restoredSettings = {
      settings: blueprintSettings.map(setting => ({
        ...setting,
        params: setting.params?.map(param => ({
          ...param,
          value: node.data.settings?.[param.key] || param.value,
        })),
      })),
    };

    return {
      ...node,
      data: {
        ...node.data,
        ...restoredSettings,
      },
      hidden: node.type === FlowNodeTypes.ROOT,
      selected: false,
      position: {
        x: 0,
        y: 0,
      },
      measured: {
        height: 0,
        width: 0,
      },
    };
  });
};

export const syncNodeActions = async (
  nodes: Node[],
): Promise<ProcessedNode[]> => {
  const allActions = nodes
    .map(node => ({
      nodeId: node.id,
      info: (node.data.settings as FlowNodeSetting).action,
    }))
    .filter(action => action.info?.action_id);

  await Promise.all(
    allActions.map(async action => {
      if (action.info.action_id) {
        try {
          const actionData = await getAction(action.info.action_id as string);

          if (actionData && Object.keys(actionData).length > 0) {
            const node = nodes.find(
              node => node.id === action.nodeId,
            ) as unknown as ProcessedNode;
            if (node) {
              const actionNodeData = actionCustomTransform.makeRecordModel(
                actionData as CustomAction & BaseAction,
              );

              const {nodeData, ...actionWithoutNodeData} = actionNodeData;
              const newAction =
                actionData.type === ActionTypeModel.BOOKING ||
                actionData.type === ActionTypeModel.TRANSFER
                  ? {...actionData}
                  : {
                      ...nodeData,
                      actionRecord: {...actionWithoutNodeData},
                    };
              node.data.settings.action = newAction;
            }
          }
        } catch (e) {
          if (e.status === 404) {
            const node = nodes.find(
              node => node.id === action.nodeId,
            ) as unknown as Node<FlowNodeData>;

            (node.data.settings as unknown as FlowNodeSetting).action = {};
            (node.data as FlowNodeData).actionError = {
              status: "not_found",
              message: `Action "${action.info.name}" not found`,
            };
            return;
          }
          if (!(e instanceof ApiError) || e.status !== 404) {
            throw e;
          }
        }
      }
    }),
  );

  return [...nodes] as unknown as ProcessedNode[];
};

/**
 * Serializes flow data (nodes and edges) to JSON string, filtering out UI-specific properties
 * that shouldn't be persisted (selected, measured, position)
 */
export const serializeFlowData = (nodes: Node[], edges: Edge[]): string => {
  return JSON.stringify({nodes, edges}, (key, value) =>
    key === "selected" || key === "measured" || key === "position"
      ? undefined
      : value,
  );
};
